/**
 * Hardware Detail hook using React Relay
 *
 * This hook fetches detailed hardware information including nested relationships,
 * maintenance data, and rental counts for the hardware detail page.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useHardwareDetailRelayQuery as UseHardwareDetailRelayQuery } from './__generated__/useHardwareDetailRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for single hardware with all detail fields
 */
const HardwareDetailQuery = graphql`
  query useHardwareDetailRelayQuery($id: ID!) {
    getHardware(id: $id) {
      # Core fields
      id
      oldId
      mac
      imei
      iccid
      label
      number
      state
      batteryPercent
      batteryVoltage
      charging
      firmwareVersion
      hasLatestFirmwareVersion
      lat
      lng
      lastSeen
      isAvailable
      isConnected
      isUnlocked
      activeRentalId
      locationId
      locationOldId
      createdOn
      vendorId
      vendorQrCode
      vendorSerial
      hardwareProductId
      hardwareProductOldId
      hardwareProductVersionId
      hardwareProductVersionOldId
      organizationId
      systemId
      systemOldId

      # Computed fields
      hasLowBattery
      hasOpenSafetyTicket
      isActive
      isInRental
      isOnline
      openMaintenanceTickets
      overdueMaintenanceTickets
      rentalCount
      unavailabilityReasons

      # Nested objects
      hardwareType {
        id
        name
      }
      location {
        id
        name
      }
      hardwareProduct {
        id
        oldId
        name
        hasMac
        hasImei
        hasBatteryStatus
        hasLocationTracking
        hasNumber
        hasTcpConnection
        hasVendorId
        hasVendorSerial
        primaryIdentifier
        supportsBleUnlock
        supportsRemoteBatteryLock
        supportsRemoteBatteryStatus
        supportsRemoteBatteryUnlock
        supportsRemoteLocationCheck
        supportsRemoteRing
        supportsRemoteUnlock
        vendor
        vendorSerialDescription
        latestFirmwareVersion
      }
      hardwareProductVersion {
        id
        oldId
        name
      }
      activeRental {
        id
        oldId
        startTime
        userId
      }

      # Relationships
      parentHardwareAssignments {
        id
        createdOn
        removedOn
        hardwareChildId
        hardwareParentId
        parent {
          id
          oldId
          number
          label
          state
          mac
          imei
          vendorId
          vendorSerial
          hardwareType {
            id
            name
          }
          hardwareProduct {
            primaryIdentifier
          }
        }
      }
      qrCode {
        id
      }
      childHardwareAssignments {
        id
        createdOn
        removedOn
        hardwareChildId
        hardwareParentId
        child {
          id
          oldId
          number
          label
          state
          mac
          imei
          vendorId
          vendorSerial
          hardwareType {
            id
            name
          }
          hardwareProduct {
            primaryIdentifier
          }
        }
      }
      maintenanceUsageStatus {
        current
        threshold
        ticketId
        ticketCreatedOn
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * Hardware detail type from GraphQL - includes all detail fields
 */
export interface HardwareDetailGraphQL {
  id: string;
  oldId?: string | null;
  mac?: string | null;
  imei?: string | null;
  iccid?: string | null;
  label?: string | null;
  number?: number | null;
  state?: number | null;
  batteryPercent?: number | null;
  batteryVoltage?: number | null;
  charging?: boolean | null;
  firmwareVersion?: string | null;
  hasLatestFirmwareVersion?: boolean | null;
  lat?: number | null;
  lng?: number | null;
  lastSeen?: string | null;
  isAvailable?: boolean | null;
  isConnected?: boolean | null;
  isUnlocked?: boolean | null;
  activeRentalId?: string | null;
  locationId?: string | null;
  locationOldId?: string | null;
  createdOn: string;
  vendorId?: string | null;
  vendorQrCode?: string | null;
  vendorSerial?: string | null;
  hardwareProductId: string;
  hardwareProductOldId: string;
  hardwareProductVersionId?: string | null;
  hardwareProductVersionOldId?: string | null;
  organizationId?: string | null;
  systemId?: string | null;
  systemOldId?: string | null;

  // Computed fields
  hasLowBattery?: boolean | null;
  hasOpenSafetyTicket?: boolean | null;
  isActive?: boolean | null;
  isInRental?: boolean | null;
  isOnline?: boolean | null;
  openMaintenanceTickets: number;
  overdueMaintenanceTickets: number;
  rentalCount: number;
  // Json scalar; server-computed map of reason id => operator-facing description.
  unavailabilityReasons?: Record<string, string> | null;

  // Nested objects
  hardwareType?: {
    id: string;
    name?: string | null;
  } | null;
  location?: {
    id: string;
    name: string;
  } | null;
  hardwareProduct?: {
    id: string;
    oldId?: string | null;
    name?: string | null;
    hasMac?: boolean | null;
    hasImei?: boolean | null;
    hasBatteryStatus?: boolean | null;
    hasLocationTracking?: boolean | null;
    hasNumber?: boolean | null;
    hasTcpConnection?: boolean | null;
    hasVendorId?: boolean | null;
    hasVendorSerial?: boolean | null;
    primaryIdentifier?: string | null;
    supportsBleUnlock?: boolean | null;
    supportsRemoteBatteryLock?: boolean | null;
    supportsRemoteBatteryStatus?: boolean | null;
    supportsRemoteBatteryUnlock?: boolean | null;
    supportsRemoteLocationCheck?: boolean | null;
    supportsRemoteRing?: boolean | null;
    supportsRemoteUnlock?: boolean | null;
    vendor?: string | null;
    vendorSerialDescription?: string | null;
    latestFirmwareVersion?: string | null;
  } | null;
  hardwareProductVersion?: {
    id: string;
    oldId?: string | null;
    name?: string | null;
  } | null;
  activeRental?: {
    id: string;
    oldId?: string | null;
    startTime?: string | null;
    userId?: string | null;
  } | null;

  qrCode?: {
    id: string;
  } | null;

  // Relationships
  parentHardwareAssignments?: Array<{
    id: string;
    createdOn: string;
    removedOn?: string | null;
    hardwareChildId?: string | null;
    hardwareParentId?: string | null;
    parent?: {
      id: string;
      oldId?: string | null;
      number?: number | null;
      label?: string | null;
      state?: number | null;
      mac?: string | null;
      imei?: string | null;
      vendorId?: string | null;
      vendorSerial?: string | null;
      hardwareType?: {
        id: string;
        name?: string | null;
      } | null;
      hardwareProduct?: {
        primaryIdentifier?: string | null;
      } | null;
    } | null;
  }>;
  childHardwareAssignments?: Array<{
    id: string;
    createdOn: string;
    removedOn?: string | null;
    hardwareChildId?: string | null;
    hardwareParentId?: string | null;
    child?: {
      id: string;
      oldId?: string | null;
      number?: number | null;
      label?: string | null;
      state?: number | null;
      mac?: string | null;
      imei?: string | null;
      vendorId?: string | null;
      vendorSerial?: string | null;
      hardwareType?: {
        id: string;
        name?: string | null;
      } | null;
      hardwareProduct?: {
        primaryIdentifier?: string | null;
      } | null;
    } | null;
  }>;

  maintenanceUsageStatus?: {
    current: number;
    threshold: number;
    ticketId?: string | null;
    ticketCreatedOn?: string | null;
  } | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching hardware detail using Relay with Suspense
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function HardwareDetail({ id }: { id: string }) {
 *   return (
 *     <Suspense fallback={<HardwareDetailSkeleton />}>
 *       <HardwareDetailContent id={id} />
 *     </Suspense>
 *   );
 * }
 *
 * function HardwareDetailContent({ id }: { id: string }) {
 *   const hardware = useHardwareDetailRelay(id);
 *   return <div>{hardware?.label}</div>;
 * }
 */
export function useHardwareDetailRelay(hardwareId: string) {
  const data = useLazyLoadQuery<UseHardwareDetailRelayQuery>(HardwareDetailQuery, {
    id: hardwareId,
  });

  return data.getHardware as HardwareDetailGraphQL | null;
}

// ============================================================================
// Non-Suspense Hook (with loading state)
// ============================================================================

/**
 * Hook that provides React Query-like interface for hardware detail
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch, isFetched } = useHardwareDetailRelayQuery(hardwareId);
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.label}</div>;
 */
export function useHardwareDetailRelayQuery(
  hardwareId: string | undefined,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    onError?: (error: Error) => void;
  }
) {
  const [data, setData] = useState<HardwareDetailGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(hardwareId);
  const refetchInterval = options?.refetchInterval;

  // Use refs to avoid recreating doFetch on every render
  const isFetchedRef = useRef(isFetched);
  const onErrorRef = useRef(options?.onError);

  // Keep refs in sync
  useEffect(() => {
    isFetchedRef.current = isFetched;
  }, [isFetched]);

  useEffect(() => {
    onErrorRef.current = options?.onError;
  }, [options?.onError]);

  // Memoize the fetch function with stable dependencies
  const doFetch = useCallback(async () => {
    if (!hardwareId) return;

    try {
      if (isFetchedRef.current) {
        setIsRefetching(true);
      } else {
        setIsLoading(true);
      }

      const result = await fetchQuery<UseHardwareDetailRelayQuery>(
        relayEnvironment,
        HardwareDetailQuery,
        { id: hardwareId }
      ).toPromise();

      if (result) {
        setData(result.getHardware as HardwareDetailGraphQL | null);
        setError(null);
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch hardware');
      setError(errorObj);
      onErrorRef.current?.(errorObj);
    } finally {
      setIsLoading(false);
      setIsFetched(true);
      setIsRefetching(false);
    }
  }, [hardwareId]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    doFetch();
  }, [enabled, hardwareId, fetchCount, doFetch]);

  // Set up refetch interval
  useEffect(() => {
    if (!enabled || !refetchInterval || refetchInterval <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      doFetch();
    }, refetchInterval);

    return () => clearInterval(intervalId);
  }, [enabled, refetchInterval, doFetch]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      isFetched,
      isRefetching,
      error,
      refetch,
    }),
    [data, isLoading, isFetched, isRefetching, error, refetch]
  );
}
